<template>
  <div class="attachment-list">
    <div class="empty" v-if="!attachments || (attachments && attachments.length === 0)">
      <small>{{ $t('no_files_found') }}</small>
    </div>
    <div class="attachment-grid-container" v-if="attachments" :class="{ inline: inline }">
      <div
        class="attachment-grid"
        v-for="attachment in sortedAttachments"
        :key="attachment.id"
        :class="{ inline: inline }"
      >
        <a v-if="attachment.filename" class="image" @click.prevent="openUrl(attachment)"
          ><img :src="fileImage(attachment.filename)"
        /></a>
        <div class="attachment-details">
          <a class="text small" @click.prevent="openUrl(attachment)">{{
            attachmentName(attachment)
          }}</a>
          <small
            v-if="!inline && attachmentMetadata(attachment)"
            class="attachment-metadata text-muted"
          >
            {{ attachmentMetadata(attachment) }}
          </small>
        </div>
        <b-button
          v-if="deletable && isDeleteVisibleToUser"
          @click.prevent="onDeleteAttachment(attachment.id)"
          :disabled="buttonsDisabled"
          variant="outline-danger"
        >
          <i class="fa fa-sm fa-times"></i>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { canWriteField, showToGuest } from '@/helpers/userCapabilities';

export default {
  name: 'AttachmentList',
  components: {},
  props: {
    parentId: String,
    attachments: Array,
    attachmentType: String,
    buttonsDisabled: { default: false },
    deletable: { default: true },
    signed: { default: true },
    sortbyName: { default: false },
    inline: { default: false },
    /** False: a guest sees delete like any user (block levels decide, switch on). */
    hidesDeleteForGuests: { type: Boolean, default: true },
  },
  data() {
    return {
      imagePath: '/img/filetypes/',
      guestVisibleSections: {
        deleteAttachment: false,
      },
    };
  },
  computed: {
    isDeleteVisibleToUser() {
      return (
        !this.hidesDeleteForGuests ||
        this.showToGuest('deleteAttachment', this.guestVisibleSections)
      );
    },
    sortedAttachments() {
      const attachments = this.attachments;

      if (!this.sortbyName) {
        return attachments;
      }

      return attachments.sort((a, b) => {
        if (
          a.displayFilename &&
          b.displayFilename &&
          a.displayFilename.toUpperCase() < b.displayFilename.toUpperCase()
        ) {
          return -1;
        }
        if (
          a.displayFilename &&
          b.displayFilename &&
          a.displayFilename.toUpperCase() > b.displayFilename.toUpperCase()
        ) {
          return 1;
        }
        return 0;
      });
    },
  },
  methods: {
    ...mapActions(['fetchSignedUrl']),
    async openSignedUrl(attachment, windowReference) {
      const url = await this.fetchSignedUrl({
        attachmentId: attachment.id,
        downloadType: 'inline',
      });
      // console.log(url);
      // window.open(url);
      windowReference.location = url;
    },
    openUrl(attachment) {
      let windowReference = window.open('about:blank', '_blank');
      if (this.signed) {
        this.openSignedUrl(attachment, windowReference);
      } else {
        // window.open(attachment.path);
        windowReference.location = attachment.path;
      }
    },
    onDeleteAttachment(attachmentId) {
      const eventPayload = {
        attachmentId: attachmentId,
        attachmentType: this.attachmentType,
      };
      if (this.parentId) {
        eventPayload.parentId = this.parentId;
      }

      this.$emit('attachment-list:delete', eventPayload);
    },
    fileExtension(filename) {
      const filetypes = [
        'xml',
        'zip',
        'xls',
        'xlsx',
        'xlsm',
        'doc',
        'docx',
        'pdf',
        'jpg',
        'png',
        'ppt',
        'pptx',
        'csv',
      ];
      const fileType = filename.split('.').pop();
      return filetypes.includes(fileType) ? fileType : 'file';
    },
    fileImage(filename) {
      return this.imagePath + this.fileExtension(filename) + '.svg';
    },
    attachmentName(attachment) {
      return attachment.displayFilename ? attachment.displayFilename : attachment.filename;
    },
    formatFileSizeMb(size) {
      if (size == null || size === '') {
        return null;
      }

      const bytes = Number(size);
      if (!Number.isFinite(bytes) || bytes < 0) {
        return null;
      }

      const megabytes = bytes / (1024 * 1024);
      if (megabytes < 0.01) {
        return '< 0.01 MB';
      }

      return `${megabytes.toFixed(2)} MB`;
    },
    formatAttachmentDate(createdAt) {
      if (createdAt == null || createdAt === '') {
        return null;
      }

      const createdAtString = String(createdAt).trim();
      const numericTimestamp = Number(createdAtString);
      const isNumericTimestamp = Number.isFinite(numericTimestamp) && /^\d+$/.test(createdAtString);

      const parsedDate = isNumericTimestamp
        ? this.$dayjs(numericTimestamp)
        : this.$dayjs(createdAt);

      if (!parsedDate.isValid()) {
        return null;
      }

      return parsedDate.format('DD.MM.YY HH:mm');
    },
    attachmentMetadata(attachment) {
      const metadataParts = [
        this.formatFileSizeMb(attachment.size),
        this.formatAttachmentDate(attachment.createdAt),
      ].filter(Boolean);

      return metadataParts.join(' · ');
    },
    canWriteField,
    showToGuest,
  },
};
</script>

<style scoped>
.attachment-grid {
  display: grid;
  grid-template-columns: 35px auto 35px;
  row-gap: 0.5em;
  margin-top: 0.5em;
  column-gap: 0.5em;
  vertical-align: middle;
}
.attachment-grid img {
  /* height: 30px; */
}
.attachment-grid button {
  padding: 0 3px;
  margin-left: 8px;
  top: 1px;
  position: relative;
  height: 23px;
  width: 23px;
}
.attachment-grid a.text {
  padding-top: 0;
}
.attachment-details {
  min-width: 0;
  padding-top: 0px;
}
.attachment-metadata {
  display: block;
  font-size: 0.75rem;
  line-height: 1.2;
  margin-top: 0.15rem;
}
.empty {
  margin-top: 0.5em;
}
.text {
  overflow: hidden;
}

/* Inline display styles */
.attachment-grid-container.inline {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.attachment-grid.inline {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.2em;
  padding: 0.25rem 0.5rem;
}

.attachment-grid.inline .text {
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.attachment-grid.inline button {
  height: 20px;
  width: 20px;
  padding: 0;
  margin-left: 0.25rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .attachment-grid-container {
    display: grid;
    row-gap: 0em;
    column-gap: 0.5em;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
  .attachment-grid {
    grid-template-columns: 35px auto;
  }

  .attachment-grid-container.inline {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
